import { useCallback, useEffect, useMemo, useState } from 'react'

import { SecondaryBase } from './../Commons/SecondaryBase'

import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'

import {GetMessagesPage as BLGetMessagesPage, GetNewerMessages as BLGetNewerMessages, ToggleMessageHide as BLToggleMessageHide, 
    ToggleMessageShown as BLToggleMessageShown, DeleteMessage as BLDeleteMessage, SelectMessage as BLSelectMessage, 
    DeselectMessage as BLDeselectMessage, SetMessageDesign as BLSetMessageDesign, ResetMessages as BLResetMessages, 
    SetOverlayPosition as BLSetOverlayPosition} from './../../../utilities/BL/Admin/Overlays/Messages'
import { Confirm } from './../../Commons/Modals/Confirm'
import { UseMessagePlayer } from './Messages/UseMessagePlayer'


// ==============================================================================
// CONFIGURACIÓN
// ==============================================================================
const PAGE_SIZE = 30;

const MODAL_TYPES = {
    DELETE: 'DELETE',
    RESET: 'RESET',
    PROJECT: 'PROJECT',
    CLEAR_SCREEN: 'CLEAR_SCREEN',
    CLEAR_QUEUE: 'CLEAR_QUEUE',
    TOGGLE_LIVE: 'TOGGLE_LIVE'
};

// Crear y poner la imagen del diseño (ej. regalo, rosas, etc.)
const AVAILABLE_DESIGNS = [
    { id: 1, name: 'Diseño 1', url: '/img/overlays/messages/1.png' },
    { id: 2, name: 'Diseño 2', url: '/img/overlays/messages/2.png' },
    { id: 3, name: 'Diseño 3', url: '/img/overlays/messages/3.png' },
    { id: 4, name: 'Diseño 4', url: '/img/overlays/messages/4.png' },
    { id: 5, name: 'Diseño 5', url: '/img/overlays/messages/5.png' },
    { id: 6, name: 'Diseño 6', url: '/img/overlays/messages/6.png' },
    { id: 7, name: 'Diseño 7', url: '/img/overlays/messages/7.png' },
    { id: 8, name: 'Diseño 8', url: '/img/overlays/messages/8.png' },
    { id: 9, name: 'Diseño 9', url: '/img/overlays/messages/9.png' }
];

const getDesignUrl = (id) => {
    const found = AVAILABLE_DESIGNS.find(d => d.id === id);
    return found ? found.url : '/img/overlays/messages/1.jpg'; 
};

// Función para formatear la fecha: 11/02/2026 17:33:11 hrs
const formatFullDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const formatted = date.toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    return `Fecha: ${formatted} hrs`;
};

const Messages = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();
    const [messages, setMessages] = useState([]);

    // ----------------------------------------------------------------------------
    // 1. INICIALIZACIÓN DEL REPRODUCTOR
    // ----------------------------------------------------------------------------
    const playerActions = useMemo(() => ({
        sendToScreen: async (item) => {
            const resp = await BLSelectMessage(item.id);
            if (!resp.success) throw new Error(resp.message);
            setMessages(prev => prev.map(m => m.id === item.id ? { ...m, wasShown: true, isSelected: true } : { ...m, isSelected: false }));
        },
        clearScreen: async () => {
            await BLDeselectMessage();
            setMessages(prev => prev.map(m => ({ ...m, isSelected: false })));
        }
    }), []); 

    const player = UseMessagePlayer(playerActions);
    const currentQItem = player.queue[player.currentIndex];

    // ----------------------------------------------------------------------------
    // 2. ESTADOS
    // ----------------------------------------------------------------------------
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState('DESC'); //DESC
    
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ type: '', data: null });
    const [verticalPos, setVerticalPos] = useState(0);

    // ----------------------------------------------------------------------------
    // 3. CARGA DE DATOS
    // ----------------------------------------------------------------------------
    const loadInitialData = async () => {
        setLoading(true);
        try {
            const resp = await BLGetMessagesPage({ limit: PAGE_SIZE, cursor: null, sort: sortOrder });
            if(!resp.success) throw new Error(resp.message);
            setMessages(resp.data.items || []);
            setNextCursor(resp.data.nextCursor);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mensajes actualizados.' });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message });
        } finally {
            setLoading(false);
        }
    };

    const loadMoreHistory = async () => {
        if (!nextCursor) {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay más mensajes por cargar' });
            return;
        }
        if (loading) return;

        setLoading(true);
        try {
            const resp = await BLGetMessagesPage({ limit: PAGE_SIZE, cursor: nextCursor, sort: sortOrder });
            if(!resp.success) throw new Error(resp.message);
            setMessages(prev => [...prev, ...resp.data.items]);
            setNextCursor(resp.data.nextCursor);
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar historial.' });
        } finally {
            setLoading(false);
        }
    };

    const checkNewMessages = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const maxId = messages.length > 0 ? Math.max(...messages.map(m => m.id)) : 0;
            const resp = await BLGetNewerMessages(maxId);
            if(!resp.success) throw new Error(resp.message);
            const newItems = resp.data || [];
            if (newItems.length > 0) {
                setMessages(prev => [...newItems, ...prev]);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `${newItems.length} mensajes nuevos` });
            } else {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'No hay mensajes nuevos' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar mensajes nuevos' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadInitialData(); }, [sortOrder]);

    // ----------------------------------------------------------------------------
    // 4. SOLICITUDES DE MODAL
    // ----------------------------------------------------------------------------
    const requestProject = (msg) => {
        if (msg.isHidden) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Mensaje oculto.' }); return; }
        setModalConfig({ type: MODAL_TYPES.PROJECT, data: msg });
        setShowModal(true);
    };
    const requestClearScreen = () => { setModalConfig({ type: MODAL_TYPES.CLEAR_SCREEN, data: null }); setShowModal(true); };
    const requestClearQueue = () => { setModalConfig({ type: MODAL_TYPES.CLEAR_QUEUE, data: null }); setShowModal(true); };
    const requestToggleLive = () => { setModalConfig({ type: MODAL_TYPES.TOGGLE_LIVE, data: { isGoingLive: !player.isLive } }); setShowModal(true); };
    const requestDelete = (msg) => { setModalConfig({ type: MODAL_TYPES.DELETE, data: msg }); setShowModal(true); };
    const requestReset = () => { setModalConfig({ type: MODAL_TYPES.RESET, data: null }); setShowModal(true); };

    // ----------------------------------------------------------------------------
    // 5. EJECUCIÓN DE ACCIONES
    // ----------------------------------------------------------------------------
    const executeProject = useCallback(async () => {
        const msg = modalConfig.data;
        if (!msg) return;
        if (player.isLive) player.toggleLive(); 
        try {
            await BLSelectMessage(msg.id);
            setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isSelected: true, wasShown: true } : { ...m, isSelected: false }));
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Proyectando.' });
        } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error.' }); } finally { setShowModal(false); }
    }, [modalConfig.data, player, showMessage]);

    const executeClearScreen = useCallback(async () => {
        if (player.isLive) player.toggleLive(); // Apagar live si está encendido
        else await playerActions.clearScreen();
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pantalla limpia.' });
        setShowModal(false);
    }, [player, playerActions, showMessage]);

    const executeToggleLive = useCallback(() => {
        player.toggleLive();
        setShowModal(false);
    }, [player]);

    const executeClearQueue = useCallback(() => {
        player.clearQueue();
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Fila vaciada.' });
        setShowModal(false);
    }, [player, showMessage]);

    const executeDelete = useCallback(async () => {
        const msg = modalConfig.data;
        if (!msg) return;
        try {
            await BLDeleteMessage(msg.id);
            setMessages(prev => prev.filter(m => m.id !== msg.id));
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Eliminado.' });
        } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error API.' }); } finally { setShowModal(false); }
    }, [modalConfig.data, showMessage]);

    const executeReset = useCallback(async () => {
        try {
            await BLResetMessages();
            setMessages([]);
            player.clearQueue();
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reiniciado.' });
        } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error.' }); } finally { setShowModal(false); }
    }, [player, showMessage]);

    // --- Helpers Directos ---
    const handleAddToQueue = (msg) => {
        if (msg.isHidden) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Mensaje oculto.' }); return; }
        player.addToQueue(msg);
    };
    const toggleHide = async (msg) => {
        try {
            setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isHidden: !m.isHidden } : m));
            await BLToggleMessageHide(msg.id);
        } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error API.' }); }
    };
    const toggleShown = async (msg) => {
        try {
            setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, wasShown: !m.wasShown } : m));
            await BLToggleMessageShown(msg.id);
        } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error API.' }); }
    };
    const updateDesign = async (msgId, designId) => {
        try {
            setMessages(prev => prev.map(m => m.id === msgId ? { ...m, designId: designId } : m));
            await BLSetMessageDesign(msgId, designId);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Diseño actualizado.' });
        } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error API.' }); }
    };
    // const handlePositionChange = async (e) => {
    //     const val = Number(e.target.value);
    //     setVerticalPos(val);
    //     await BLSetOverlayPosition(val);
    // };

    // --- Control de Posición Y ---
    
    // 1. Para el deslizador: Actualiza el estado y manda a la API de inmediato
    const handleSliderChange = async (e) => {
        const val = Number(e.target.value);
        setVerticalPos(val);
        await BLSetOverlayPosition(val);
    };

    // 2. Para el cuadro de texto: Solo actualiza el estado local (lo que ves) mientras escribes
    const handleInputChange = (e) => {
        setVerticalPos(Number(e.target.value));
    };

    // 3. Para aplicar el cambio manualmente (Botón o Enter)
    const applyInputPosition = async () => {
        await BLSetOverlayPosition(verticalPos);
    };

    // 4. Para detectar la tecla Enter
    const handleInputKeyDown = async (e) => {
        if (e.key === 'Enter') {
            await applyInputPosition();
        }
    };

    const ReturnToDashboard = async () => {
        try { await BLDeselectMessage(); getOutOfTheDynamic(); } catch { getOutOfTheDynamic(); }
    };

    // ----------------------------------------------------------------------------
    // 6. RENDERIZADO
    // ----------------------------------------------------------------------------
    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            
            <p className='Admin__Messages__Text--Title text-center'><i className='bi bi-chat-left-quote'></i> Mensajes de apoyo</p>
            
            {/* Header Limpio (Sin botón Actualizar) */}
            <div className='d-flex justify-content-center align-items-center mt-3'>
                {/* <div>
                    <p className='Admin__Messages__Text--Title'><i className='bi bi-chat-left-quote'></i> Mensajes</p>
                    <p className='Admin__Messages__Text--Small'>Total cargados: {messages.length}</p>
                </div> */}

                <button type="button" className='Btn__Adsl--RedOutlineTransparent mb-0 me-4' onClick={requestClearScreen} title="Limpiar Pantalla"><i className='bi bi-x-square'></i> Quitar mensaje de la pantalla</button>
                {/* <button type="button" className='Btn__Adsl--RedOutlineTransparent' onClick={requestReset} title="Borrar todo"><i className='bi bi-trash-fill'></i> Borrar todos los mensajes</button> */}
                {/* <button type="button" className='Btn__Adsl--Disabled' title="Borrar todo"><i className='bi bi-trash-fill'></i> Borrar todos los mensajes</button> */}
            </div>

            {/* Reproductor */}
            <div className='Admin__Messages__PlayerContainer'>
                <div className='Admin__Messages__Player'>
                    {/* DECK */}
                    <div className='Admin__Messages__Player__Deck'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                            <span className={`badge ${player.isLive ? 'bg-success' : 'bg-danger'}`}>{player.isLive ? 'En vivo' : 'Fuera del aire'}</span>
                            <span className='small'>{player.projectionStatus}</span>
                        </div>

                        <div className='Admin__Messages__Player__Deck__Controls mt-3'>
                            <div className='d-flex justify-content-between align-items-center'>
                                <div className='d-flex align-items-center gap-2'>
                                    <small>Intervalo:</small>
                                    <select className='form-select form-select-sm w-auto bg-dark text-white border-secondary' value={player.autoPlayInterval} onChange={(e) => player.setAutoPlayInterval(Number(e.target.value))}>
                                        <option value={3000}>3s</option>
                                        <option value={5000}>5s</option>
                                        <option value={8000}>8s</option>
                                        <option value={10000}>10s</option>
                                        <option value={15000}>15s</option>
                                        <option value={20000}>20s</option>
                                    </select>
                                </div>
                                <button type='button' className={`btn btn-sm ${player.isLive ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={requestToggleLive}>
                                    <i className='bi bi-broadcast'></i> {player.isLive ? 'Detener proyección' : 'Iniciar proyección'}
                                </button>
                            </div>
                        </div>

                        <div className='Admin__Messages__Player__Deck__Screen mt-3'>
                            {currentQItem ? (
                                <div className='Admin__Messages__Player__Deck__Screen__Content'> {/* style={{ backgroundImage: `url(${getDesignUrl(currentQItem.designId)})` }} */}
                                    <div className="Admin__Messages__Player__Deck__Screen__Content__Text">
                                        <p className="msg-body">"{currentQItem.message}"</p>
                                        <p className="msg-sender">- {currentQItem.sender}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className='Admin__Messages__Player__Deck__Screen__Empty'>Fila vacía</div>
                            )}
                        </div>
                        
                        {/* BARRA DE PROGRESO ELIMINADA */}
                        
                        <div className='Admin__Messages__Player__Deck__Controls mt-3'>
                            <div className='d-flex justify-content-center gap-2 mb-2'>
                                <button type='button' className='Btn__Adsl--RedOutlineTransparent mb-0' onClick={player.playPrev}><i className='bi bi-skip-backward-fill'></i></button>
                                <button type='button' className={`Btn__Adsl--${player.isPlaying ? 'Red' : 'RedOutlineTransparent'} mb-0`} onClick={player.togglePlay}><i className={`bi bi-${player.isPlaying ? 'pause-fill' : 'play-fill'}`}></i></button>
                                <button type='button' className='Btn__Adsl--RedOutlineTransparent' onClick={player.playNext}><i className='bi bi-skip-forward-fill'></i></button>
                            </div>
                            {/* <div className='d-flex justify-content-between align-items-center'>
                                <div className='d-flex align-items-center gap-2'>
                                    <small>Intervalo:</small>
                                    <select className='form-select form-select-sm w-auto bg-dark text-white border-secondary' value={player.autoPlayInterval} onChange={(e) => player.setAutoPlayInterval(Number(e.target.value))}>
                                        <option value={3000}>3s</option>
                                        <option value={5000}>5s</option>
                                        <option value={8000}>8s</option>
                                        <option value={10000}>10s</option>
                                        <option value={15000}>15s</option>
                                        <option value={20000}>20s</option>
                                    </select>
                                </div>
                                <button type='button' className={`btn btn-sm ${player.isLive ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={requestToggleLive}>
                                    <i className='bi bi-broadcast'></i> {player.isLive ? 'Detener proyección' : 'Iniciar proyección'}
                                </button>
                            </div> */}
                        </div>
                    </div>

                    {/* PLAYLIST */}
                    <div className='Admin__Messages__Player__Playlist'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                            <span className='fw-bold'>Fila ({player.queue.length})</span>
                            <button type='button' className='btn btn-link btn-sm text-danger text-decoration-none' onClick={requestClearQueue} disabled={player.queue.length === 0}>Vaciar</button>
                        </div>
                        <div className='Admin__Messages__Player__Playlist__List'>
                            {player.queue.length === 0 ? (<p className='text-center small mt-4'>Agrega mensajes desde la biblioteca</p>) : (
                                player.queue.map((item, idx) => (
                                    <div key={item._qid} className={`Admin__Messages__Player__Playlist__Item ${idx === player.currentIndex ? 'active' : ''}`} onClick={() => player.jumpTo(idx)}>
                                        <button type='button' className='btn btn-sm text-danger' onClick={(e) => { e.stopPropagation(); player.removeFromQueue(idx); }} title="Quitar"><i className='bi bi-trash'></i></button>
                                        <div className="text-truncate flex-grow-1">
                                            <small className="fw-bold d-block">#{item.id} - {item.sender}</small>
                                            <small className="text-truncate d-block">{item.message}</small>
                                        </div>
                                        <div className='d-flex flex-column ms-2'>
                                            <button type='button' className='btn btn-sm p-0 text-white' onClick={(e) => { e.stopPropagation(); player.reorderQueue(idx, idx - 1); }} title="Subir"><i className='bi bi-chevron-up'></i></button>
                                            <button type='button' className='btn btn-sm p-0 text-white' onClick={(e) => { e.stopPropagation(); player.reorderQueue(idx, idx + 1); }} title="Bajar"><i className='bi bi-chevron-down'></i></button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className='d-flex flex-column align-items-center mt-5 mb-5'>
                <div className=''>
                    <p className='mb-0 text-center'>Posición 'Y' de la barra en pantalla:</p>
                    
                    {/* Control Deslizable */}
                    <input type="range" className="d-block form-range w-100" min="0" max="645" step="5" value={verticalPos} onChange={handleSliderChange} />

                    <p className='text-center mb-0'>--- o ---</p>

                    {/* Cuadro de texto numérico */}
                    <input type="number" className="d-block form-control text-center border-secondary w-100" value={verticalPos} onChange={handleInputChange} onKeyDown={handleInputKeyDown} title="Presiona Enter para aplicar" />
                </div>
                
                {/* Botón de Aplicar */}
                <button type="button" className="Btn__Adsl--RedOutlineTransparent mt-3" onClick={applyInputPosition} title="Aplicar posición manual">Aplicar</button>
            </div>


            <div className='border border-secondary rounded pt-5 pb-5 mt-4'>
                <p className='text-center mb-0'>Total de mensajes: {messages.length}</p>
                <p className='text-center small'>Orden: Del más reciente al más antigüo</p>

                {/* <div className='d-flex justify-content-end px-4 mt-4 align-items-center'>
                    <span className="me-2 text-white small">Ordenar:</span>
                    <div className="btn-group btn-group-sm">
                        <button type="button" className={`btn ${sortOrder === 'ASC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setSortOrder('ASC')}>Antiguos</button>
                        <button type="button" className={`btn ${sortOrder === 'DESC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setSortOrder('DESC')}>Nuevos</button>
                    </div>
                </div> */}

                <div className='d-flex justify-content-center px-4 mt-4 align-items-center'>
                    <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={checkNewMessages}>
                        <i className='bi bi-arrow-clockwise'></i> Buscar nuevos mensajes
                    </button>
                </div>

                <section className='Admin__Messages__Library Admin__Messages__SpaceUp'>
                    {messages.length > 0 ? messages.map((msg) => (
                        <div key={msg.id} className={`Admin__Messages__Library__Message ${msg.isHidden ? 'opacity-50' : ''} ${msg.isSelected ? 'Admin__Messages__Library__Message--Active' : ''}`} style={msg.wasShown ? { border: '2px solid #28a745' } : {}}>
                            <div className='Admin__Messages__Library__Message__ActionButtons'>
                                <button type='button' className='Btn__Adsl--RedOutlineTransparent Admin__Messages__Library__Message__ActionButtons__Button' disabled={msg.isHidden} onClick={() => handleAddToQueue(msg)} title="Agregar a fila"><i className='bi bi-plus-lg'></i></button>
                                <button type='button' className={`Btn__Adsl--RedOutlineTransparent Admin__Messages__Library__Message__ActionButtons__Button ${msg.isHidden ? 'text-warning' : ''}`} onClick={() => toggleHide(msg)}><i className={`bi ${msg.isHidden ? 'bi-eye-slash-fill' : 'bi-eye'}`}></i></button>
                                <button type='button' className={`Btn__Adsl--RedOutlineTransparent Admin__Messages__Library__Message__ActionButtons__Button ${msg.wasShown ? 'text-success' : ''}`} onClick={() => toggleShown(msg)}><i className={`bi ${msg.wasShown ? 'bi-check-circle-fill' : 'bi-circle'}`}></i></button>
                                {/* <button type='button' className='Btn__Adsl--RedOutlineTransparent Admin__Messages__Library__Message__ActionButtons__Button' onClick={() => requestDelete(msg)}><i className='bi bi-trash'></i></button> */}
                                <button type='button' className='Btn__Adsl--Disabled Admin__Messages__Library__Message__ActionButtons__Button'><i className='bi bi-trash'></i></button>
                            </div>
                            <div className="flex-grow-1 d-flex flex-column justify-content-center text-center my-2">
                                <p className='Admin__Messages__Text--Small mb-1'>#{msg.id}</p>
                                <p className='Admin__Messages__Text--Small'>{formatFullDate(msg.date || msg.createdAtUtc)} </p>
                                <p className="fw-bold fs-5 mb-2">"{msg.message}"</p>
                                <p className="fst-italic text-info">- {msg.sender || "Anónimo"}</p>
                                {/* Teléfono / WhatsApp (Solo si existe) */}
                                {/* {msg.phone && (
                                    <p>
                                        <i className="bi bi-whatsapp text-success me-1"></i>{msg.phone}<i className="bi bi-phone text-success me-1"></i>
                                    </p>
                                )} */}
                            </div>
                            <div className="mt-auto">
                                {/* <p className="mb-1 small">Diseño:</p>
                                <div className='Admin__Messages__Library__Message__OptionList'>
                                    <button type='button' className={`Btn__Adsl--RedOutlineTransparent Admin__Messages__Library__Message__OptionList__Option ${msg.designId === 0 ? 'bg-secondary text-white' : ''}`} onClick={() => updateDesign(msg.id, 0)} title="Aleatorio"><i className='bi bi-shuffle'></i></button>
                                    {AVAILABLE_DESIGNS.map((design) => (
                                        <button key={design.id} type='button' className={`Btn__Adsl--RedOutlineTransparent Admin__Messages__Library__Message__OptionList__Option ${msg.designId === design.id ? 'border-primary' : ''}`} onClick={() => updateDesign(msg.id, design.id)}>
                                            <img src={design.url} className='Admin__Messages__Library__Message__OptionList__Option__PreviewImage' alt="" />
                                        </button>
                                    ))}
                                </div> */}
                                <button type="button" className={`Btn__Adsl--${msg.isSelected ? 'Success' : 'RedOutlineTransparent'} w-100 mt-2`} onClick={() => msg.isSelected ? requestClearScreen() : requestProject(msg)} disabled={msg.isHidden}>
                                    {msg.isSelected ? 'Quitar' : 'Proyectar ahora'}
                                </button>
                            </div>
                        </div>
                    )) : (<div className="w-100 text-center py-5"><p className="text-muted">No hay mensajes.</p></div>)}
                </section>

                {/* BOTÓN CARGAR MÁS + BOTÓN ACTUALIZAR LISTA (COMBINADOS ABAJO) */}
                <div className="d-flex justify-content-center gap-3 my-4">
                    <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={checkNewMessages}>
                        <i className='bi bi-arrow-clockwise'></i> Buscar nuevos mensajes
                    </button>
                    {/* <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={loadMoreHistory} disabled={loading}>
                        {loading ? 'Cargando...' : 'Cargar historial anterior'}
                    </button> */}
                    {/* <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={loadMoreHistory} disabled={loading}>
                        {loading ? (
                            'Cargando...'
                        ) : (
                            <>
                                <i className='bi bi-arrow-clockwise'></i> Buscar nuevos mensajes
                            </>
                        )}
                    </button> */}
                </div>
            </div>


            {/* <button type="button" className='Btn__Adsl--RedOutlineTransparent' onClick={requestReset} title="Borrar todo"><i className='bi bi-trash-fill'></i> Borrar todos los mensajes</button> */}
            <button type="button" className='Btn__Adsl--Disabled mx-auto mt-5 mb-4' title="Borrar todo"><i className='bi bi-trash-fill'></i> Borrar todos los mensajes</button>

            {showModal && (
                <>
                    {modalConfig.type === MODAL_TYPES.DELETE && (<Confirm message={`¿Deseas borrar el mensaje #${modalConfig.data?.id}?`} actionToYesButton={executeDelete} textYesButton="Sí, borrar" textNoButton="Cancelar" setShowModal={setShowModal} setConfirmData={() => {}} />)}
                    {modalConfig.type === MODAL_TYPES.RESET && (<Confirm message="¿Deseas borrar TODOS los mensajes? (Esto no se puede deshacer)" actionToYesButton={executeReset} textYesButton="Sí, borrar todo" textNoButton="Cancelar" setShowModal={setShowModal} setConfirmData={() => {}} />)}
                    {modalConfig.type === MODAL_TYPES.PROJECT && (<Confirm message={`¿Deseas proyectar el mensaje de ${modalConfig.data?.sender || 'Anónimo'}?`} actionToYesButton={executeProject} textYesButton="Sí, proyectar" textNoButton="Cancelar" setShowModal={setShowModal} setConfirmData={() => {}} />)}
                    {modalConfig.type === MODAL_TYPES.CLEAR_SCREEN && (<Confirm message="¿Deseas quitar el mensaje de la pantalla?" actionToYesButton={executeClearScreen} textYesButton="Sí, quitar" textNoButton="Cancelar" setShowModal={setShowModal} setConfirmData={() => {}} />)}
                    {modalConfig.type === MODAL_TYPES.CLEAR_QUEUE && (<Confirm message="¿Deseas vaciar toda la fila de reproducción?" actionToYesButton={executeClearQueue} textYesButton="Sí, vaciar fila" textNoButton="Cancelar" setShowModal={setShowModal} setConfirmData={() => {}} />)}
                    {modalConfig.type === MODAL_TYPES.TOGGLE_LIVE && (<Confirm message={modalConfig.data.isGoingLive ? "¿Deseas iniciar la proyección en vivo?" : "¿Deseas detener la proyección en vivo?"} actionToYesButton={executeToggleLive} textYesButton={modalConfig.data.isGoingLive ? "Sí, iniciar proyección" : "Sí, detener proyección"} textNoButton="Cancelar" setShowModal={setShowModal} setConfirmData={() => {}} />)}
                </>
            )}
        </SecondaryBase>
    );
};

export { Messages };